<script setup>
import { onBeforeUnmount, ref, watch } from 'vue';
import CopilotEditor from 'dashboard/components/widgets/WootWriter/CopilotEditor.vue';
import CaptainLoader from 'dashboard/components/widgets/conversation/copilot/CaptainLoader.vue';
import CaptainTasksAPI from 'dashboard/api/captain/tasks';

const props = defineProps({
  showCopilotEditor: {
    type: Boolean,
    default: false,
  },
  isGeneratingContent: {
    type: Boolean,
    default: false,
  },
  generatedContent: {
    type: String,
    default: '',
  },
  conversationId: {
    type: [String, Number],
    default: '',
  },
});

const emit = defineEmits([
  'focus',
  'blur',
  'clearSelection',
  'contentReady',
  'send',
]);

const copilotEditorContent = ref('');
const chineseTranslation = ref('');
const isTranslating = ref(false);
let translationController;

const translateGeneratedContent = async content => {
  translationController?.abort();
  chineseTranslation.value = '';
  if (!content?.trim()) return;

  const requestController = new AbortController();
  translationController = requestController;
  isTranslating.value = true;

  try {
    const response = await CaptainTasksAPI.translateToChinese(
      { content, conversationId: props.conversationId },
      requestController.signal
    );
    if (requestController.signal.aborted) return;
    chineseTranslation.value = response.data.message?.trim() || '';
  } catch (error) {
    if (error?.name !== 'CanceledError' && error?.name !== 'AbortError') {
      chineseTranslation.value = '';
    }
  } finally {
    if (translationController === requestController) {
      isTranslating.value = false;
      translationController = undefined;
    }
  }
};

watch(
  () => props.generatedContent,
  content => translateGeneratedContent(content),
  { immediate: true }
);

onBeforeUnmount(() => translationController?.abort());

const onFocus = () => {
  emit('focus');
};

const onBlur = () => {
  emit('blur');
};

const clearEditorSelection = () => {
  emit('clearSelection');
};

const onSend = () => {
  emit('send', copilotEditorContent.value);
  copilotEditorContent.value = '';
};
</script>

<template>
  <Transition
    mode="out-in"
    enter-active-class="transition-all duration-300 ease-out"
    enter-from-class="opacity-0 translate-y-2 scale-[0.98]"
    enter-to-class="opacity-100 translate-y-0 scale-100"
    leave-active-class="transition-all duration-200 ease-in"
    leave-from-class="opacity-100 translate-y-0 scale-100"
    leave-to-class="opacity-0 translate-y-2 scale-[0.98]"
    @after-enter="emit('contentReady')"
  >
    <CopilotEditor
      v-if="showCopilotEditor && !isGeneratingContent"
      key="copilot-editor"
      v-model="copilotEditorContent"
      class="copilot-editor"
      :generated-content="generatedContent"
      :chinese-translation="chineseTranslation"
      :is-translating="isTranslating"
      :min-height="4"
      :enabled-menu-options="[]"
      @focus="onFocus"
      @blur="onBlur"
      @clear-selection="clearEditorSelection"
      @send="onSend"
    />
    <div
      v-else-if="isGeneratingContent"
      key="loading-state"
      class="bg-n-iris-5 rounded min-h-[4.75rem] w-full mb-4 p-4 flex items-start"
    >
      <div class="flex items-center gap-2">
        <CaptainLoader class="text-n-iris-10 size-4" />
        <span class="text-sm text-n-iris-10">
          {{ $t('CONVERSATION.REPLYBOX.COPILOT_THINKING') }}
        </span>
      </div>
    </div>
  </Transition>
</template>

<style lang="scss">
.copilot-editor {
  .ProseMirror-menubar {
    display: none;
  }
}
</style>
